import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { isInModuleScoped } from '../constants/ship-type.constants';
import { Module } from '../enums/modules.enum';

/** Names the module a request comes from; the server widens its ship-type scope on it (ModuleShipScopeAttribute). */
const MODULE_HEADER = 'X-Videck-Module';

@Injectable({ providedIn: 'root' })
export class BaseApiService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;
  private readonly keycloak = inject(Keycloak);
  private readonly router: Router = inject(Router);

  public getAuthHeaders(): HttpHeaders {
    const keycloak = this.keycloak;
    const token = keycloak?.token;
    let headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
    const module = this.inModuleScopedModule();
    if (module) {
      headers = headers.set(MODULE_HEADER, module);
    }
    return headers;
  }

  /**
   * The module of the current URL when it has in-module ship types (IN_MODULE_SHIP_TYPES); other
   * modules need no header. Read from the URL, including a navigation still in flight: components on
   * a freshly entered module route load their data before NavigationEnd, so
   * AppStateService.activeModule would still name the previous module.
   */
  private inModuleScopedModule(): Module | null {
    const url = this.router.currentNavigation()?.finalUrl ?? this.router.parseUrl(this.router.url);
    const segment = url.root.children['primary']?.segments[0]?.path;
    return isInModuleScoped(segment) ? segment : null;
  }

  get<T>(endpoint: string, params?: HttpParams, context?: HttpContext): Observable<T> {
    const headers = this.getAuthHeaders();
    return this.http.get<T>(`${this.baseUrl}${endpoint}`, { params, context, headers });
  }

  /**
   * GET an endpoint that returns a plain-text body. Use for actions that return a bare `string`
   * (e.g. `Ok("...")`), which ASP.NET serves as `text/plain` — the default JSON `get<T>` would fail
   * to parse those and error out.
   */
  getText(endpoint: string, params?: HttpParams, context?: HttpContext): Observable<string> {
    const headers = this.getAuthHeaders();
    return this.http.get(`${this.baseUrl}${endpoint}`, { params, context, headers, responseType: 'text' });
  }

  post<T>(endpoint: string, body?: unknown, params?: HttpParams, context?: HttpContext): Observable<T> {
    const headers = this.getAuthHeaders();
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, body, { params, context, headers });
  }

  delete<T>(endpoint: string, params?: HttpParams, context?: HttpContext): Observable<T> {
    const headers = this.getAuthHeaders();
    return this.http.delete<T>(`${this.baseUrl}${endpoint}`, { params, context, headers });
  }

  put<T>(endpoint: string, body: unknown, params?: HttpParams, context?: HttpContext): Observable<T> {
    const headers = this.getAuthHeaders();
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, body, { params, context, headers });
  }

  patch<T>(endpoint: string, body: unknown, params?: HttpParams, context?: HttpContext): Observable<T> {
    const headers = this.getAuthHeaders();
    return this.http.patch<T>(`${this.baseUrl}${endpoint}`, body, { params, context, headers });
  }
}
